import React, { useRef, useEffect, useCallback, useLayoutEffect, useState, useMemo } from "react";
import { Grid, useTheme, styled, alpha, Theme } from "@mui/material";
import { CrewScheduleGL, IHoveredEvent, baseRowHeight } from "./gl/CrewScheduleGL";
import { InteractiveState } from "src/redux/reducers/FlightTimelineReducer";
import { ScrollBarWrapper } from "src/SharedGL/ScrollBar/ScrollBarWrapper";
import {
  ICrewPairingPosition,
  ICrewPairingMap,
  ICrewNonFlyingAssignmentMap,
  CrewSchedEvent,
  ICrewEvent,
  defaultTimezone,
  IBaseTime,
  defaultCrewBase,
  ICrewPairing,
  ICrewMember,
} from "src/redux/reducers/CrewScheduleReducer";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { BaseTimelineGL } from "../../SharedGL/BaseTimelineGL";
import { useSelector, useDispatch } from "react-redux";
import {
  findPairingsFromTime,
  findPairingsFromTime2,
  findCrewEventFromTime,
  getOverlappingEvents,
  assignPairingToCrew,
} from "src/redux/actions/CrewSchedule/pairings";
import {
  setSelectedCrewEvents,
  setSelectedCrewDays,
  setHoveredEvents,
  setFocusedCrewSchedPairing,
  setTimelineLink,
  deselectCollapseAllRows,
  toggleSelectedCrewMemberAction,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { SelectedCrewEvents } from "src/components/CrewSchedule/SelectedCrewEvents";
import { setHoveredTime, setInteractiveState } from "src/redux/actions/flights/FlightTimeline.Actions";
import { HoveredTime } from "../FlightTimeline/HoveredTime";
import {
  ScrollBarClick,
  isIPairingPosition,
  IPairingPosition,
  IBidPackageResponse,
  IDisplayBidPairing,
  IDisplayBidNFA,
  IBidPackageNonFlyingAssignments,
  IPosition,
  ICrew,
} from "@vmosolutions/oc-interfaces"; // IUser
import { openContextMenu } from "src/redux/actions/ContextMenu.Actions";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { SelectedCrewDays } from "src/components/CrewSchedule/SelectedCrewDays";
import { handleWheel, handleKeyPress, crewScheduleAxisLimitInMilliseconds } from "./CrewScheduleInputHandler";
import { HoveredCrewSchedEvents } from "./HoveredCrewSchedEvents";
import { timeScales } from "src/pages/FlightTimeline/TimelineUtils";
import moment from "moment-timezone";
import { PairingsGL } from "./gl/PairingsGL";
import { NonFlyingAssignmentsGL } from "./gl/NonFlyingAssignmentsGL";
import { CrewScheduleQualifications } from "./CrewScheduleQualifications";
import { CrewScheduleViolations } from "./CrewScheduleViolations";
import { CrewScheduleErrorsGL } from "./gl/CrewScheduleErrorsGL";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { useDrop, DropTargetMonitor } from "react-dnd";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { IModalInfo } from "./CrewScheduleWindow";
import { crewBidRowHeight } from "src/components/CrewSchedule/CrewMemberBidding";
import { updateCrewBidEvent, createBiddingNFACounts, setBidNFACounts } from "src/redux/actions/Bidding.Actions";
import { WarningModal } from "src/components/ModalComponents/WarningModal";
import { IconButton } from "src/components/IconButton/IconButton";
import Link from "@mui/icons-material/Link";
import LinkOpen from "@mui/icons-material/LinkOff";
import { FlightTimelineDatePicker } from "../FlightTimeline/FlightTimelineDatePicker";
import { IOpenFlightRequestPairing, returnCrewPairingAsOpenFlight } from "./CrewScheduleHeader/CrewOpenFlights";

interface ICrewScheduleCanvasProps {
  id: string;
  crew: ICrewPairingPosition[];
  crewPairings: ICrewPairingMap;
  crewNFAs: ICrewNonFlyingAssignmentMap;
  crewListRef: React.MutableRefObject<HTMLDivElement>;
  // crewTime: ICrewTimeOption;
  hideHoveredTimeIndicator?: boolean;
  scrollBarOnlyOnHover?: boolean;
  initialStartTime: number;
  initialEndTime: number;
  liveLoad?: boolean;
  isCameraLocked?: boolean;
  headerChanged?: boolean | string;
  path: string[];
  dataLoadedCheck?: (minTime: number, maxTime: number) => void;
  disableEventSummary?: boolean;
  /** disables expanded rows and hides other items not required for display in the editor like planned events, NFAs, crew/day LMS etc. */
  pairingEditorView?: boolean;
  setScrollToDate?: React.Dispatch<React.SetStateAction<(jumpDate: Date) => void>>;
  setActiveEventModal?: (modalInfo: IModalInfo) => void;
  crewPosition?: string;
  bidRankSettings?: [{ prefRank: number; label: string; color: string }]; // based off of IBidSettingsValue.ranks
  selectedBidPackage?: IBidPackageResponse;
  selectedBidNFATypes?: IBidPackageNonFlyingAssignments[];
  setCloseWarning?: (warning: string) => void;
}

export interface IAnchorSelection {
  anchorPos: { x: number; y: number };
  crewId: string;
  time: number;
}

export interface ICrewMemberGLDimensions {
  rowStart: number;
  rowHeight: number;
  isExpanded: boolean;
}

const OCCrewScheduleCanvas = styled(Grid)(({ theme }: { theme: Theme }) => ({
  width: "100%",
  flexGrow: 1,
  minHeight: "0px",
  minWidth: "0px",
  flexBasis: "0px",
  overflow: "hidden",
  "& .crew_schedGL": {
    width: "100%",
    flexGrow: 1,
    minHeight: "0px",
    minWidth: "0px",
    flexBasis: "0px",
    overflow: "hidden",
    position: "relative",
  },
  "& .loadingData": {
    width: "200px",
    height: "80px",
    position: "absolute",
    opacity: 1.0,
    bottom: `${60 - theme.custom.statusBar.height}px`,
    right: `${ScrollBarWrapper.scrollBarWidth + 30}px`,
    "& .dialog_spinner_progress": {
      backgroundColor: alpha(theme.palette.common.black, 0.75),
      borderRadius: 3,
      "& svg.oc_spinner": {
        marginLeft: 10,
        width: "54px",
        height: "unset",
      },
      "& .spinner_message": { margin: "0px 10px" },
      "& span.animation": { fontWeight: 300 },
    },
  },
}));

let isAnimating = false;
export const CrewScheduleCanvas = (props: ICrewScheduleCanvasProps): JSX.Element => {
  const mount = useRef<HTMLDivElement>(null);
  const frameIdRef = useRef<number>(0);
  const dispatch = useDispatch();
  const {
    hideHoveredTimeIndicator,
    crewPairings,
    crewNFAs,
    initialStartTime,
    initialEndTime,
    liveLoad,
    isCameraLocked,
    headerChanged,
    dataLoadedCheck,
    crew,
    crewListRef,
    id,
    path,
    disableEventSummary,
    pairingEditorView,
    setScrollToDate,
    setActiveEventModal,
    crewPosition,
    selectedBidPackage,
    bidRankSettings,
    selectedBidNFATypes,
    setCloseWarning,
  } = props;

  const theme = useTheme();
  const crewSchedGL = useRef<CrewScheduleGL | null>(null);
  const [lastUpdatedCrew, setLastUpdatedCrew] = useState(0);

  const [hoveredViolation] = useState<IAnchorSelection | null>(null);

  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]);
  const crewScheduleHideToolTips = useSelector(
    (state: IRootState) => state.authentication.appSettings.crewScheduleHideTooltips
  );
  const hideNfaPriority = useSelector(
    (state: IRootState) => state.crewSchedule.biddingSettings?.hideNfaPriority ?? false
  );
  const crewBase = windowProperties?.crewBase ?? defaultCrewBase;
  const displayTimezone = windowProperties?.displayTimezone ?? defaultTimezone;
  const focusCrewSchedArea = windowProperties?.focusCrewSchedArea;
  const shouldDeselectRef = useRef<boolean>(true);
  const canUpdateHoveredEvent = useRef<boolean>(true);
  const [captureMode, setCaptureMode] = useState<boolean>(false);
  const [pointerId, setPointerId] = useState<number>(0);

  const crewVios = useSelector((state: IRootState) => state.crewSchedule.crewViolations);
  const loadedViolations = useSelector((state: IRootState) => state.crewSchedule.loadedViolations);
  const crewViolations = useMemo(() => (!loadedViolations ? null : crewVios), [crewVios, loadedViolations]);
  const crewDayLogicModifiers = useSelector((state: IRootState) => state.crewSchedule.crewDayLogicModifiers);
  const crewPlannedEvents = useSelector((state: IRootState) => state.crewSchedule.crewPlannedEvents);

  const isICAO = useSelector((state: IRootState) => state.authentication.appSettings.blockInfo.icao);
  //!! Watch out because only a limited number of airports are stored in the redux store.
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  // Maintain a count of how many times webGL loses it's context
  // This happens as new ones are created as browsers have a max, restore any active windows by incrementing this count and forcing component remount.
  const [webGLLostContextCount, setWeblGLLostContextCount] = useState<number>(0);
  const [initializedContext, setInitializedContext] = useState<number>(-1);

  // Bidding related data.
  const crewBids = useSelector((state: IRootState) => state.bidding.crewBids);
  const biddingNFACounts = useSelector((state: IRootState) => state.bidding.biddingNFACounts);
  const bidAssignmentIsDirty = useSelector((state: IRootState) => state.bidding.assignmentIsDirty);
  const awardedBidsVisible = useSelector((state: IRootState) => state.bidding.awardedBidsVisible);
  const selectedBiddingCrewMember: ICrewMember | null = useMemo(
    () =>
      (crew as (IPairingPosition | ICrewMember)[])?.find((c) => {
        const crewMember = !isIPairingPosition(c) ? c : null;
        return crewMember?.isSelected;
      }) as ICrewMember | null,
    [crew]
  );

  const interactiveState = useSelector((state: IRootState) => state.timelineFlights.interactiveState[id]);

  // const focusCrewSchedArea = useSelector((state: IRootState) => state.crewSchedule.focusCrewSchedArea);
  // const loadedInterval = useSelector((state: IRootState) => state.crewSchedule.timeIntervalLoaded);

  const focusCrewSchedPairing = useSelector((state: IRootState) => state.crewSchedule.focusCrewSchedPairing);
  const temporaryPairings = useSelector((state: IRootState) => state.crewSchedule.temporaryPairings);
  // Active Open Flying Pairing Request
  const openFlightRequest = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]?.openFlying);

  // const intervalStart = useMemo(() => loadedInterval?.startOfMonth ?? 0, [loadedInterval]);
  // const intervalEnd = useMemo(() => loadedInterval?.endOfMonth ?? 0, [loadedInterval]);

  const selectedCrewEvents = useSelector((state: IRootState) => state.crewSchedule.selectedCrewEvents);
  const crewDataLoaded = useSelector((state: IRootState) => state.crewSchedule.crewDataLoaded);
  const selectedCrewDays = useSelector((state: IRootState) => state.crewSchedule.selectedCrewDays);
  //const [crewCanvasFocus, setCrewCanvasFocus] = useState<boolean>(false);
  const [showTimelineDatePickers, setShowTimelineDatePickers] = useState<boolean>(false);

  // Returns count of awarded NFAs on load (assigned get added later) and store in redux.
  useEffect(() => {
    if (!selectedBidPackage) {
      return;
    }
    const biddingNFACounts = createBiddingNFACounts(selectedBidPackage, crewNFAs);
    if (biddingNFACounts) dispatch(setBidNFACounts(biddingNFACounts));
  }, [dispatch, selectedBidPackage, crewNFAs]);

  /**
   * When displaying NFA bid counts in bidding mode, filter out NFA types that are not selected.
   */
  const biddingSelectedNFACounts = useMemo(() => {
    if (biddingNFACounts && !!Object.values(biddingNFACounts)?.length) {
      const selectedNFATypeTitles = selectedBidNFATypes?.map((nfa) => nfa?.nfaType?.title);
      const selectedNFAcounts = Object.entries(biddingNFACounts).reduce((obj: { [key: string]: any }, nfaList) => {
        const [dayKey, nfaTypesObj] = nfaList;
        const NFATypesArray = Object.entries(nfaTypesObj);
        const seletedNFATypes =
          !!NFATypesArray?.length &&
          NFATypesArray?.reduce((nfaTypesObj: { [key: string]: any }, nfa) => {
            const [nfaIDTitle, nfaCounts] = nfa;
            if (selectedNFATypeTitles?.includes(nfaIDTitle)) {
              nfaTypesObj[nfaIDTitle] = nfaCounts; // ONLY include NFA types that are actively selected.
            }
            return nfaTypesObj;
          }, {});
        obj[dayKey] = seletedNFATypes;
        return obj;
      }, {});
      return selectedNFAcounts;
    } else {
      return biddingNFACounts;
    }
  }, [biddingNFACounts, selectedBidNFATypes]);

  // Height of the selected NFA Types (in bidding mode) - needed to ensure hover is accurate.
  const biddingNFARowHeight = useMemo(() => {
    if (biddingSelectedNFACounts) {
      const selectedNFACount = Object.values(Object.values(biddingSelectedNFACounts)?.[0])?.length;
      const assignedNFACount = selectedNFACount > 0 ? selectedNFACount : 1; // If bidding is active, set mininum height to 1 row;
      return assignedNFACount * BaseTimelineGL.NFAcountRowHeight + BaseTimelineGL.biddingWindowBarHeight;
    } else {
      return 0;
    }
  }, [biddingSelectedNFACounts]);

  const hideCheckInCheckOutTimes = useMemo(() => {
    return windowProperties?.hideCheckInCheckOutTimes !== undefined ? windowProperties?.hideCheckInCheckOutTimes : true;
  }, [windowProperties]);

  const hideTimeBetweenEvents = useMemo(() => {
    return windowProperties?.hideTimeBetweenEvents !== undefined ? windowProperties?.hideTimeBetweenEvents : true;
  }, [windowProperties]);

  // type StartFunction = () => void;
  let clickedOnScroll: ScrollBarClick = 1;

  const exitCaptureMode = (): void => {
    if (pointerId) mount?.current?.releasePointerCapture(pointerId);
    setCaptureMode(false);
    setPointerId(0);
  };

  const enterCaptureMode = (pointerId: number): void => {
    setPointerId(pointerId);
    mount?.current?.setPointerCapture(pointerId);
    setCaptureMode(true);
  };

  const handleMouseDown = (event: React.MouseEvent): void => {
    isAnimating = true;
    shouldDeselectRef.current = true;
    // interactionMode = InteractiveState.Pan;
    const yPos = event.clientY - mount?.current?.getBoundingClientRect()?.top;
    if (yPos > 0 && yPos < 60) {
      // If user clicks on top 60 pixels of the timeline (the time axis), display the date range pickers.
      setShowTimelineDatePickers(true);
    }

    start();

    //if you are clicking on the scroll bar - move it
    const rect = mount?.current?.getBoundingClientRect();

    clickedOnScroll = crewSchedGL?.current?.scrollClick(
      (event.clientX - rect.left) * window.devicePixelRatio,
      event.clientY - rect.top - (BaseTimelineGL.timelineStart + biddingNFARowHeight)
    );
    //should return the type of interactive state...
    if (clickedOnScroll === ScrollBarClick.None) {
      dispatch(setInteractiveState(InteractiveState.Pan, id));
    } else if (clickedOnScroll === ScrollBarClick.ClickedOnThumb) {
      dispatch(setInteractiveState(InteractiveState.ScrollBarThumb, id));
    } else {
      dispatch(setInteractiveState(InteractiveState.ScrollBar, id));
    }
  };

  const scrollAircraftView = useCallback(() => {
    if (!crewSchedGL.current || !crewListRef.current) {
      return;
    }
    const [yMin] = crewSchedGL.current.getYRange();
    crewListRef.current.scrollTop = yMin;
  }, [crewListRef]);

  const timelineLink = useSelector((state: IRootState) => state.crewSchedule.timelineLink);
  const timelinesLinked = useMemo(() => !!timelineLink?.activeWindowId, [timelineLink]);

  const handleMouseEnter = useCallback(
    (event: any): void => {
      // when mouse enters the canvas, if the timelines are set in link mode, update the active window to this one.
      if (timelineLink?.activeWindowId && timelineLink?.activeWindowId !== id) {
        dispatch(setTimelineLink(timelineLink.start, timelineLink.end, id));
      }
    },
    [dispatch, id, timelineLink]
  );

  // #region Animation
  const animate = useCallback(() => {
    if (crewSchedGL.current) {
      crewSchedGL.current.draw();
      scrollAircraftView();
      if (isAnimating) {
        frameIdRef.current = window.requestAnimationFrame(animate);
        if (!!timelineLink?.activeWindowId && timelineLink?.activeWindowId === id) {
          const [start, end] = crewSchedGL.current.getTimeRange();
          dispatch(setTimelineLink(start, end, id));
        }
      }
    }
  }, [scrollAircraftView, timelineLink?.activeWindowId, id, dispatch]);

  // If there's linked timeline info and NOT the active timeline, set time axis.
  useEffect(() => {
    if (crewSchedGL?.current && !!timelineLink?.activeWindowId && timelineLink?.activeWindowId !== id) {
      const { start, end } = timelineLink;
      crewSchedGL?.current?.setTimeAxis(start, end);
      crewSchedGL?.current?.draw();
    }
  }, [timelineLink, id]);

  const start = useCallback(() => {
    frameIdRef.current = requestAnimationFrame(animate);
  }, [animate]);

  // SHIFT/CTRL Event selection handler.
  const updateSelectedEvents = useCallback(
    (mouseEvent: React.MouseEvent, hoveredEvents: ICrewEvent[], alreadySelected?: boolean): void => {
      const { ctrlKey, shiftKey } = mouseEvent;
      // If user right clicks on an items that already exists in the selection, simply open the context menu.
      if (mouseEvent.button === 2 && alreadySelected) {
        // OPEN CONTEXT MENU
        dispatch(openContextMenu(`${id}`));
      } else {
        dispatch(setSelectedCrewEvents(hoveredEvents, shiftKey, ctrlKey, false));
      }
    },
    [dispatch, id]
  );

  const handleMouseUp = useCallback(
    (mouseEvent: React.MouseEvent): void => {
      isAnimating = false;
      //check if where the mouse has ended is in the month(s) of loaded Data
      const { ctrlKey, shiftKey } = mouseEvent;

      if (liveLoad && crewSchedGL?.current && dataLoadedCheck && typeof dataLoadedCheck === "function") {
        const [minTime, maxTime] = crewSchedGL.current.getTimeRange();
        dataLoadedCheck(minTime, maxTime);
      }

      //check if you are hovering on thumb bar
      const rect = mount?.current?.getBoundingClientRect();
      crewSchedGL?.current?.hoveredOnScrollBarThumb(
        (mouseEvent.clientX - rect.left) * window.devicePixelRatio,
        mouseEvent.clientY - rect.top - (BaseTimelineGL.timelineStart + biddingNFARowHeight)
      );

      // interactionMode = InteractiveState.None;
      dispatch(setInteractiveState(InteractiveState.None, id));
      if (!shouldDeselectRef.current) {
        return;
      }

      const hoveredEvents: ICrewEvent[] = crewSchedGL?.current?.getHoveredEvents();

      if (hoveredEvents?.length > 0 && (mouseEvent.button === 0 || mouseEvent.button === 2)) {
        const itemIndex: number = selectedCrewEvents.findIndex((event: ICrewEvent) => {
          return event.event?._id === hoveredEvents[0].event?._id;
        });

        const isSingleBidEvent =
          hoveredEvents?.length === 1 &&
          (hoveredEvents[0]?.event as IDisplayBidPairing | IDisplayBidNFA).hasOwnProperty("prefRank");

        if (isSingleBidEvent) {
          const isBidPairing = (hoveredEvents[0]?.event as IDisplayBidPairing)?.bidPairingId;
          const updatedBidEvent: IDisplayBidPairing | IDisplayBidNFA = {
            ...(hoveredEvents[0]?.event as IDisplayBidPairing | IDisplayBidNFA),
            assigned: !(hoveredEvents[0]?.event as IDisplayBidPairing | IDisplayBidNFA).assigned,
          };
          const updatedHoveredEvent: ICrewEvent = { ...hoveredEvents[0], event: updatedBidEvent };
          if (itemIndex === -1) {
            if (mouseEvent.button === 0) {
              dispatch(updateCrewBidEvent(updatedHoveredEvent, isBidPairing ? "pairing" : "nfa")); // Toggle assignment of bid pairing/nfa.
              dispatch(setSelectedCrewEvents([updatedHoveredEvent], shiftKey, ctrlKey, false));
            } else {
              dispatch(setSelectedCrewEvents(hoveredEvents, shiftKey, ctrlKey, false));
            }
          } else if (mouseEvent.button === 0) {
            dispatch(updateCrewBidEvent(updatedHoveredEvent, isBidPairing ? "pairing" : "nfa")); // Toggle assignment of bid pairing/nfa.
            selectedCrewEvents.splice(itemIndex, 1);
            dispatch(setSelectedCrewEvents(selectedCrewEvents, shiftKey, ctrlKey, false));
          }
          // update the hovered event as well so that user can properly toggle on/off without moving mouse.
          const [, , y] = getCrewMemberAndTimeFromMouse(mouseEvent.clientX, mouseEvent.clientY);
          crewSchedGL.current.setHoveredEvents([updatedHoveredEvent], y);
        } else {
          updateSelectedEvents(mouseEvent, hoveredEvents, itemIndex > -1);
        }
      }
      start();
    },
    [biddingNFARowHeight, dataLoadedCheck, dispatch, id, liveLoad, selectedCrewEvents, start, updateSelectedEvents]
  );

  const handleMouseMove = (event: React.MouseEvent): void => {
    switch (interactiveState) {
      case InteractiveState.Pan:
        if (isAnimating && !isCameraLocked) {
          if (Math.abs(event.movementX) > 3 || Math.abs(event.movementY) > 3) {
            shouldDeselectRef.current = false;
          }
          crewSchedGL?.current?.moveCameraByXPixels(-event.movementX);
        }
        break;
      case InteractiveState.ScrollBarThumb:
      case InteractiveState.ScrollBar:
        crewSchedGL.current.dragScrollBarThumb(event.movementY);
        break;
      default:
        break;
    }

    //check if hovering on the scroll bar
    const rect = mount?.current?.getBoundingClientRect();
    if (interactiveState !== InteractiveState.ScrollBar && interactiveState !== InteractiveState.ScrollBarThumb) {
      crewSchedGL?.current?.hoveredOnScrollBarThumb(
        (event.clientX - rect.left) * window.devicePixelRatio,
        event.clientY - rect.top - (BaseTimelineGL.timelineStart + biddingNFARowHeight)
      );
    }

    if (!disableEventSummary) updateHoveredEvent(event.clientX, event.clientY);
  };

  const handleMouseLeave = (event: any): void => {
    //when you mouse off - set the hovered events to empty
    crewSchedGL?.current?.setHoveredEvents([], 0);

    //clear thumb hover color aslong as you aren't dragging the thumb
    if (interactiveState !== InteractiveState.ScrollBarThumb) {
      crewSchedGL?.current?.hoveredOnScrollBarThumb(0, 0);
    }

    event.preventDefault();
    if (interactiveState !== InteractiveState.ScrollBarThumb) {
      stopMouseInteraction(event);
    } else {
      if (!mount) {
        return;
      } else if (!captureMode || !pointerId) {
        enterCaptureMode(event.pointerId);
      }
    }
  };

  const stopMouseInteraction = (event: React.MouseEvent): void => {
    shouldDeselectRef.current = true;
    if (interactiveState !== InteractiveState.Pan) {
      dispatch(setInteractiveState(InteractiveState.Pan, id));
      dispatch(setHoveredEvents({ hoveredEvents: [], anchorPos: { x: -1, y: -1 }, windowId: id }));
    }
    isAnimating = false;
  };

  /**
   * crew useEffect - Added new setting to disable expanded row in pairing editor.
   */
  useEffect(() => {
    if (!crewSchedGL.current) {
      return;
    }
    crewSchedGL?.current?.updateCrew(crew, pairingEditorView);
  }, [crew, pairingEditorView]);

  /**
   * Pairings useEffect
   */
  useEffect(() => {
    if (crew.length > 0 && crewPairings) {
      crewSchedGL?.current?.updatePairings(crewPairings);
      setLastUpdatedCrew(Date.now());
    }
  }, [crew, crewPairings]);

  /**
   * NFAs useEffect
   */
  useEffect(() => {
    if (crew.length > 0) {
      crewSchedGL?.current?.updateNFAs(crewNFAs);
    }
  }, [crew, crewNFAs]);

  /**
   * Overlaps useEffect
   * if the pairings or the NFAs change, check for overlaps
   */
  useEffect(() => {
    if (crew.length > 0 && crewPairings && crewNFAs) {
      crewSchedGL.current.updateCrewOverLaps(crewNFAs, crewPairings);
      crewSchedGL.current.updateCrewEvents(crewNFAs, crewPairings);
    }
  }, [crew, crewPairings, crewNFAs]);

  useEffect(() => {
    if (crew.length > 0 && !pairingEditorView) {
      crewSchedGL?.current?.updatePlannedEvents(crewPlannedEvents);
    }
  }, [crew, crewPlannedEvents, pairingEditorView]);

  useEffect(() => {
    if (crew.length > 0) {
      crewSchedGL?.current?.updateDayLogicModifiers(crewDayLogicModifiers);
    }
  }, [crew, crewDayLogicModifiers]);

  useEffect(() => {
    if (crew.length > 0 && temporaryPairings) {
      crewSchedGL?.current?.updateTemporaryPairings(temporaryPairings);
    }
  }, [crew, temporaryPairings]);

  useEffect(() => {
    crewSchedGL?.current?.setHideCheckInOutTimes(hideCheckInCheckOutTimes);
    if (!isAnimating) {
      start();
    }
  }, [start, hideCheckInCheckOutTimes]);

  useEffect(() => {
    crewSchedGL?.current?.setHideTimeBetweenEvents(hideTimeBetweenEvents);
    if (!isAnimating) {
      start();
    }
  }, [start, hideTimeBetweenEvents]);

  const updateHoveredTime = useCallback(
    (xAxisPos: number) => {
      //translate the hovered xAxis position to Epoch time
      const clientX = xAxisPos - mount?.current?.getBoundingClientRect()?.left;
      const timeHovered: number = crewSchedGL?.current?.pixelCoordsToEpochTime(clientX * window.devicePixelRatio);
      //send to redux the epoch time - from redux 'HoveredTime' component will call setHoveredTime to
      // each timeline that is open
      setTimeout(() => dispatch(setHoveredTime(timeHovered)), 33);
    },
    [dispatch]
  );

  const getCrewMemberAndTimeFromMouse = (clientX: number, clientY: number): [string, number, number] => {
    const rect = mount?.current?.getBoundingClientRect();
    const x = (clientX - rect.left) * window.devicePixelRatio;
    const y = clientY - rect.top;
    const crewId = crewSchedGL?.current?.getHoveredCrewMember(x, y);

    const timeRange = crewSchedGL?.current ? crewSchedGL.current.getTimeRange() : null;
    const minTime = timeRange ? timeRange[0] : 0;
    const pixelToAxis = crewSchedGL?.current ? crewSchedGL.current.pixelCoordsToTimeAxis(x) : 0;
    const hoveredTime = minTime + pixelToAxis;

    return [crewId, hoveredTime, y];
  };

  // Look for and return actively selected open flying request pairing, add request ids to object
  const activeOpenFlyingPairing: IOpenFlightRequestPairing | null = useMemo(() => {
    if (!openFlightRequest?.pairingID || !openFlightRequest?.requestIDs?.length || !crewPairings) {
      return null;
    }
    const openFlightRequestPairing = returnCrewPairingAsOpenFlight(
      openFlightRequest?.pairingID,
      crewPairings,
      openFlightRequest?.requestIDs
    );
    return openFlightRequestPairing;
  }, [crewPairings, openFlightRequest]);

  const updateHoveredEvent = useCallback(
    (clientX: number, clientY: number) => {
      if (!isAnimating && crewSchedGL.current && canUpdateHoveredEvent.current) {
        canUpdateHoveredEvent.current = false;
        setTimeout(() => (canUpdateHoveredEvent.current = true), 33);
        updateHoveredTime(clientX);

        const [crewId, hoveredTime, y] = getCrewMemberAndTimeFromMouse(clientX, clientY);
        if (!crewId) {
          return;
        }
        const hoveredEvents: IHoveredEvent[] = [];

        const crewMemberDim = crewSchedGL.current?.getCrewMemberDimensions(crewId);
        if (!crewMemberDim) {
          return;
        }

        const [yMin] = crewSchedGL.current.getYRange();

        const scaledTimelineStart = BaseTimelineGL.timelineStart + biddingNFARowHeight; //* window.devicePixelRatio not used here as html already applies the scaling.
        const currentY = y + yMin - crewMemberDim.rowStart - scaledTimelineStart;

        const crewBidPairings = crewBids?.[crewId]?.bids;
        const isBidEvent = currentY >= baseRowHeight + 5 && !!crewBidPairings;
        const isPlanned = currentY >= baseRowHeight && !isBidEvent;
        const isViolation = currentY >= 0 && currentY < CrewScheduleErrorsGL.yHeight && !isBidEvent;

        if (isPlanned) {
          const crewMemberPlannedEvents = crewPlannedEvents[crewId];
          if (crewMemberPlannedEvents && crewMemberPlannedEvents.plannedEvents) {
            const plannedEvents: CrewSchedEvent[] = crewMemberPlannedEvents.plannedEvents.map(
              (overlappingEvents: CrewSchedEvent[]) => overlappingEvents[0]
            );
            const [hoveredPlannedEvent, foundIndex] = findCrewEventFromTime(
              plannedEvents,
              hoveredTime,
              0,
              plannedEvents.length - 1
            );

            if (hoveredPlannedEvent) {
              crewMemberPlannedEvents.plannedEvents[foundIndex].forEach((event: CrewSchedEvent) => {
                hoveredEvents.push({
                  event,
                  crewId: crewId,
                  isPlanned,
                });
              });
            }
          }
        } else if (isViolation) {
          // Left out for now, we may need interactions with violations though in which case it'll be here
          // setHoveredViolation({ anchorPos: { x: clientX, y: clientY + 4 }, crewId, time: hoveredTime });
        } else if (isBidEvent) {
          // If user has an actively loaded crewBidPairings and is hovering in the pref rank rows.
          for (const [rank, details] of Object.entries(crewBidPairings)) {
            const rankYStart = baseRowHeight + 23 + (Number(rank) - 1) * crewBidRowHeight + (Number(rank) - 1) * 4;
            const rankYEnd = baseRowHeight + 23 + Number(rank) * crewBidRowHeight + (Number(rank) - 1) * 4;
            if (currentY >= rankYStart && currentY <= rankYEnd) {
              const bidPairings = details?.displayBidPairings;
              const bidNFAs = details?.displayBidNFAs;

              // Find bid pairings
              const [hoveredBidPairing, index] = findPairingsFromTime2(
                bidPairings,
                hoveredTime,
                0,
                bidPairings.length - 1,
                true
              );
              if (hoveredBidPairing && index >= 0 && index < bidPairings.length) {
                // If awarded and eligibleToAssign are false, bid has been awarded to another crew member so hide (optionally).
                const ineligibleBidPairing =
                  !(hoveredBidPairing as IDisplayBidPairing).awarded &&
                  !(hoveredBidPairing as IDisplayBidPairing).eligibleToAssign;
                if (!ineligibleBidPairing) {
                  hoveredEvents.push({
                    event: hoveredBidPairing,
                    crewId: crewId,
                    isPlanned,
                  });
                  hoveredEvents.push(...getOverlappingEvents(hoveredBidPairing, index, bidPairings, crewId, isPlanned));
                }
              }

              const [hoveredbidNFA] = findCrewEventFromTime(bidNFAs, hoveredTime, 0, bidNFAs?.length - 1);
              if (hoveredbidNFA) {
                hoveredEvents.push({
                  event: hoveredbidNFA,
                  crewId,
                  isPlanned,
                });
              }
            }
          }
        } else {
          const crewMemberPairings = crewPairings[crewId];
          if (
            !isBidEvent &&
            (crewMemberPairings?.pairings?.length || activeOpenFlyingPairing?._id) &&
            currentY >= PairingsGL.pairingYStart &&
            currentY <= PairingsGL.pairingHeight + PairingsGL.pairingYStart
          ) {
            // If crew member has pairings, check them
            if (crewMemberPairings?.pairings?.length > 0) {
              const [hoveredPairing, index] = findPairingsFromTime(
                crewMemberPairings.pairings,
                hoveredTime,
                0,
                crewMemberPairings.pairings.length - 1
              );
              if (hoveredPairing && index >= 0 && index < crewMemberPairings.pairings.length) {
                hoveredEvents.push({
                  event: hoveredPairing,
                  crewId,
                  isPlanned,
                });
                hoveredEvents.push(
                  ...getOverlappingEvents(hoveredPairing, index, crewMemberPairings.pairings, crewId, isPlanned)
                );
              }
            }
            // If there are open flying pairings visible on screen and hovered, push to hovered events.
            if (activeOpenFlyingPairing?._id) {
              const [hoveredOpenFlyingPairing, index] = findPairingsFromTime(
                [activeOpenFlyingPairing],
                hoveredTime,
                0,
                0
              );
              if (hoveredOpenFlyingPairing) {
                hoveredEvents.push({
                  event: hoveredOpenFlyingPairing,
                  crewId,
                  isPlanned: false,
                });
              }
            }
          }

          const crewMemberNFAs = crewNFAs[crewId];
          if (
            crewMemberNFAs &&
            crewMemberNFAs.nonFlyingAssignments &&
            currentY >= NonFlyingAssignmentsGL.yStart &&
            currentY <= NonFlyingAssignmentsGL.height + NonFlyingAssignmentsGL.yStart
          ) {
            const [hoveredNFA] = findCrewEventFromTime(
              crewMemberNFAs.nonFlyingAssignments,
              hoveredTime,
              0,
              crewMemberNFAs.nonFlyingAssignments.length - 1
            );
            if (hoveredNFA) {
              hoveredEvents.push({
                event: hoveredNFA,
                crewId,
                isPlanned,
              });
            }
          }
        }

        // const hoveredEvents;
        const currHoveredEvents = crewSchedGL.current.getHoveredEvents();
        if (currHoveredEvents?.[0]?.event?._id !== hoveredEvents?.[0]?.event?._id) {
          dispatch(setHoveredEvents({ hoveredEvents, anchorPos: { x: clientX, y: clientY + 4 }, windowId: id }));
        }
        crewSchedGL.current.setHoveredEvents(hoveredEvents, y);
        if (!isAnimating) {
          start();
        }
      }
    },
    [
      updateHoveredTime,
      biddingNFARowHeight,
      crewBids,
      crewPlannedEvents,
      crewPairings,
      crewNFAs,
      dispatch,
      id,
      start,
      activeOpenFlyingPairing,
    ]
  );

  const reInitializeWebGLContext = useCallback((event: any): void => {
    event.preventDefault();
    // Incrementing lost context count will trigger a re-initialization of the webgl context.
    setWeblGLLostContextCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    crewSchedGL.current.setAirportInfo(isICAO, airportOptions);
  }, [isICAO, airportOptions]);

  /**
   * RETURN function triggered on unmount of canvas.
   */
  useEffect(() => {
    return () => {
      if (crewSchedGL?.current?.canvas) {
        crewSchedGL.current.canvas.removeEventListener("webglcontextlost", reInitializeWebGLContext);
      }
      if (crewSchedGL?.current?.loseContextExt) {
        // The following will remove the webgl context on unmount if extension is available.
        crewSchedGL.current.loseContextExt.loseContext();
      }
    };
    //! Leave dependency array empty as we only want this return triggered by unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * New initialization function triggers created of the webgl context and sets all required buffers/settings followed by a draw call.
   * This function is triggered once when the canvas is mounted and re-triggered if webgl context is lost and needs to be restored.
   */
  const initializeWebGLCanvas = useCallback(() => {
    crewSchedGL.current = new CrewScheduleGL(mount.current, 0, 0);
    if (crewSchedGL?.current) {
      crewSchedGL.current.canvas.addEventListener("webglcontextlost", reInitializeWebGLContext);
      crewSchedGL.current.setTheme(theme.custom.timeline.webglCanvas);
      crewSchedGL.current.setTimeAxis(initialStartTime, initialEndTime);
      crewSchedGL.current.setAirportInfo(isICAO, airportOptions);
      crewSchedGL.current.updateCrew(crew, pairingEditorView);
      crewSchedGL.current.updatePairings(crewPairings);
      crewSchedGL.current.updateNFAs(crewNFAs);
      crewSchedGL.current.updateCrewOverLaps(crewNFAs, crewPairings);
      crewSchedGL.current.updateCrewEvents(crewNFAs, crewPairings);
      if (!pairingEditorView) crewSchedGL.current.updatePlannedEvents(crewPlannedEvents);
      crewSchedGL.current.updateDayLogicModifiers(crewDayLogicModifiers);
      crewSchedGL.current.updateTemporaryPairings(temporaryPairings);
      crewSchedGL.current.draw();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    crew,
    crewDayLogicModifiers,
    crewNFAs,
    crewPairings,
    crewPlannedEvents,
    initialEndTime,
    initialStartTime,
    pairingEditorView,
    reInitializeWebGLContext,
    temporaryPairings,
    theme.custom.timeline.webglCanvas,
  ]);

  // #region CrewSchedule Initialize webgl canvas
  useLayoutEffect(() => {
    if (webGLLostContextCount !== initializedContext) {
      initializeWebGLCanvas();
      setInitializedContext(webGLLostContextCount); // If these values match, context should be active, otherwise, re-initialize.
    }
  }, [initializeWebGLCanvas, webGLLostContextCount, initializedContext]);

  useEffect(() => {
    crewSchedGL?.current?.setTheme(theme.custom.timeline.webglCanvas);
  }, [theme]);

  useEffect(() => {
    crewSchedGL?.current?.setTimeAxis(initialStartTime, initialEndTime);
  }, [initialStartTime, initialEndTime]);

  // Jump to a supplied Date (if not provided, jump to current date)
  const scrollToDate = useCallback(
    (jumpDate: Date): void => {
      const jumpTime =
        typeof jumpDate === "object" && typeof jumpDate.getMonth === "function"
          ? jumpDate.getTime()
          : new Date().getTime();
      if (!crewSchedGL?.current) return;
      const [currentStart, currentEnd] = crewSchedGL.current.getTimeRange();
      const buffer = (currentEnd - currentStart) / 2;
      const currentMidPoint = Math.round((currentEnd - currentStart) / 2);
      const moveDistance = currentMidPoint - (currentMidPoint - jumpTime) - buffer;
      const pixelXDistance = crewSchedGL?.current?.timeAxisToPixelCoords(moveDistance);
      crewSchedGL?.current?.moveCameraByXPixels(pixelXDistance);

      if (!isAnimating) {
        animate();
      }
    },
    [animate]
  );

  interface IGhostPairingDrop {
    pairingInfo: {
      _id: string;
      shortId: string;
      start: number;
      end: number;
      positions: IPairingPosition[];
    };
    type: "PAIRING_EVENT";
  }

  // DISPLAY active open flying pairing request in requestor (crew) rows.
  useEffect(() => {
    const openFlyingRequestCrewMap: { [key: string]: any } = {};
    if (crewSchedGL?.current && !!activeOpenFlyingPairing?.openFlyingRequest?.length) {
      const crewRequests = activeOpenFlyingPairing?.openFlyingRequest?.map((cr: any) => cr.user_id);
      crewRequests?.forEach((user_id: string) => {
        // Check to see if this request is already assigned to this crew member (DO NOT ADD it to request buffer if it is)
        const alreadyAssigned = crewPairings[user_id]?.pairings?.find(
          (assignedPairing) => assignedPairing?._id === activeOpenFlyingPairing
        );
        if (!alreadyAssigned) {
          openFlyingRequestCrewMap[user_id] = {
            pairings: [activeOpenFlyingPairing],
            lastModified: new Date().valueOf(),
          };
        }
      });
    }
    crewSchedGL?.current.updateUnassignedPairings(openFlyingRequestCrewMap);
  }, [activeOpenFlyingPairing, crewPairings]);

  const [{ isOver }, drop] = useDrop({
    accept: ["PAIRING_EVENT"],
    drop(i: any, monitor: DropTargetMonitor) {
      const item: IGhostPairingDrop = i;
      const dropConflict = crewSchedGL?.current?.ghostPairingConflict;
      const ghostPairing = crewSchedGL?.current?.ghostPairing;
      const crewMemberId = crewSchedGL?.current?.ghostPairing?.crewId;
      const selectedCrew: any = crew.find((crewMember) => {
        const crewId = isIPairingPosition(crewMember) ? crewMember.crew_id : crewMember._id;
        return crewId === crewMemberId;
      });
      const findFirstAvailablePosition = (crewPositions: any[], ghostPairing: any, checkEmpty: boolean): any => {
        for (const position of crewPositions) {
          const availablePosition = ghostPairing.positions.find((gp: any) => {
            if (!checkEmpty) {
              return gp.position === position.title && gp.hasOwnProperty("crew_id"); // This has a crew member in it.
            }
            return gp.position === position.title && !gp.hasOwnProperty("crew_id");
          });
          if (availablePosition) {
            return position;
          }
        }
        return null;
      };

      const assignCrewPosition = findFirstAvailablePosition(selectedCrew.crewInfo.positions, ghostPairing, true);
      const assignCrewPositionfallback = findFirstAvailablePosition(
        selectedCrew.crewInfo.positions,
        ghostPairing,
        false
      );
      const actualCrewPosition = assignCrewPosition ?? assignCrewPositionfallback ?? selectedCrew.crewInfo.positions[0];

      if (ghostPairing && dropConflict && setActiveEventModal && ghostPairing?.pairingId === item?.pairingInfo?._id) {
        const modalProps = {
          pairingId: ghostPairing?.pairingId,
          assignCrewId: ghostPairing.crewId,
          assignCrewPosition: actualCrewPosition,
          conflict: dropConflict,
        };
        setActiveEventModal({ type: EventModalTypes.AssignCrewToPairing, modalProps });
      } else if (ghostPairing && !dropConflict) {
        // If no conflict, just assign the pairing
        assignPairingToCrew(ghostPairing?.crewId, ghostPairing?.pairingId);
      }
      crewSchedGL?.current?.setTempPairingOverlay(null, actualCrewPosition.title);
    },
    hover(i: any, monitor: DropTargetMonitor) {
      const item: IGhostPairingDrop = i;
      // Set the tempPairing (ghost) on crew member's row
      // Only move the camera if there's an item.
      if (item?.pairingInfo?.start) {
        if (!crewSchedGL?.current) return;
        const [currentStart, currentEnd] = crewSchedGL.current.getTimeRange();
        const buffer = (currentEnd - currentStart) / 8;
        // Only move the timeline if the current pairing start is not visible on screen.
        if (item.pairingInfo.start < currentStart || item.pairingInfo.start > currentEnd) {
          const moveDistance = currentStart - (currentStart - item.pairingInfo.start) - buffer;
          const pixelXDistance = crewSchedGL?.current?.timeAxisToPixelCoords(moveDistance);
          crewSchedGL?.current?.moveCameraByXPixels(pixelXDistance);
        }
      }

      const yPos = monitor?.getClientOffset()?.y - mount?.current?.getBoundingClientRect()?.top;
      const crewMemberId = crewSchedGL?.current?.getCrewMemberFromYPixel(yPos);
      const crewMember: any = crewSchedGL?.current?.crewList.find((cm) => {
        const whichID = isIPairingPosition(cm) ? cm.crew_id : cm._id;
        return crewMemberId === whichID;
      });
      const findFirstAvailablePosition = (crewPositions: any[], ghostPairing: any, checkEmpty: boolean): any => {
        for (const position of crewPositions) {
          const availablePosition = ghostPairing.positions.find((gp: any) => {
            if (!checkEmpty) {
              return gp.position === position.title && gp.hasOwnProperty("crew_id"); // This has a crew member in it.
            }
            return gp.position === position.title && !gp.hasOwnProperty("crew_id");
          });
          if (availablePosition) {
            return position;
          }
        }
        return null;
      };
      if (crewMember) {
        if (!isIPairingPosition(crewMember)) {
          const assignCrewPosition = findFirstAvailablePosition(crewMember.crewInfo.positions, item.pairingInfo, true);
          const assignCrewPositionfallback = findFirstAvailablePosition(
            crewMember.crewInfo.positions,
            item.pairingInfo,
            false
          );
          const actualCrewPosition =
            assignCrewPosition ?? assignCrewPositionfallback ?? crewMember.crewInfo.positions[0];
          crewSchedGL?.current?.setTempPairingOverlay(item?.pairingInfo, actualCrewPosition.title, yPos);
        } else {
          crewSchedGL?.current?.setTempPairingOverlay(item?.pairingInfo, crewMember.position, yPos);
        }
      }
    },
    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  // Remove ghost pairing.
  useEffect(() => {
    if (crewSchedGL && !isOver) {
      crewSchedGL?.current?.setTempPairingOverlay(null, crewPosition);
    }
  }, [crewSchedGL, isOver, crewPosition]);

  useEffect(() => {
    if (setScrollToDate) setScrollToDate(() => scrollToDate);
  }, [setScrollToDate, scrollToDate]);

  //focus to a time on the crew sched - move the x-axis
  const setCrewSchedTime = useCallback(
    (startTime: number, endTime: number) => {
      const start = startTime - timeScales.day;
      //make sure you aren't trying to show more than the timeline limit
      const end =
        endTime - startTime < crewScheduleAxisLimitInMilliseconds
          ? endTime + timeScales.day
          : start + crewScheduleAxisLimitInMilliseconds;

      if (!crewSchedGL?.current) return;
      // If current scale supports view of pairing, move camera, otherwise set time axis
      const [currenStart, currentEnd] = crewSchedGL.current.getTimeRange();
      if (end - start < currentEnd - currenStart && !openFlightRequest?.pairingID) {
        const buffer = (currentEnd - currenStart) / 2;
        const focusMidPoint = Math.round((end + start) / 2);
        const currentMidPoint = Math.round((currentEnd - currenStart) / 2);
        const moveDistance = currentMidPoint - (currentMidPoint - focusMidPoint) - buffer;
        const pixelXDistance = crewSchedGL?.current?.timeAxisToPixelCoords(moveDistance);
        crewSchedGL?.current?.moveCameraByXPixels(pixelXDistance);
      } else {
        // if in open flying assignment mode, always set exact times - allow zoom.
        crewSchedGL?.current?.setTimeAxis(start, end);
      }
      //
      if (liveLoad && dataLoadedCheck && typeof dataLoadedCheck === "function") {
        dataLoadedCheck(start, end);
      }
    },
    [dataLoadedCheck, liveLoad, openFlightRequest?.pairingID]
  );

  // Find a particular crew member and move camera to that location.
  const moveCameraToCrewMember = useCallback((crewId: string) => {
    if (!crewSchedGL?.current) return;
    const [yMin, yMax] = crewSchedGL.current.getYRange();
    const YCrewPos = crewSchedGL.current.getYPixelFromCrewMember(crewId);
    const inRange = YCrewPos > yMin && YCrewPos < yMax ? true : false;
    if (!inRange) {
      const currentMidPoint = yMin + (yMax - yMin) / 2;
      const pixelYDistance = YCrewPos - currentMidPoint;
      crewSchedGL?.current?.moveCameraByYPixels(pixelYDistance);
    }
  }, []);

  // If redux passes information to focus the crew sched window,
  // set timeAxis to display that area.
  //you want to make sure this is only going to happen after mounting
  useLayoutEffect(() => {
    if (focusCrewSchedArea?.timeInterval?.length === 2 && focusCrewSchedArea?.crewIds?.length > 0) {
      const crewId = focusCrewSchedArea.crewIds[0];
      setCrewSchedTime(focusCrewSchedArea?.timeInterval[0], focusCrewSchedArea?.timeInterval[1]);
      moveCameraToCrewMember(crewId);
    } else if (focusCrewSchedArea?.timeInterval?.length === 2) {
      // set Crew sched time - focus on an area but not a crew
      setCrewSchedTime(focusCrewSchedArea?.timeInterval[0], focusCrewSchedArea?.timeInterval[1]);
    } else if (focusCrewSchedArea?.timeInterval?.length === 0 && focusCrewSchedArea?.crewIds?.length === 1) {
      // Focus on a crew member but don't move sched time
      moveCameraToCrewMember(focusCrewSchedArea.crewIds[0]);
    }
  }, [focusCrewSchedArea, moveCameraToCrewMember, setCrewSchedTime]);

  // gets first available pairing from id
  const getPairingEventFromId = useCallback(
    (pairingId: string): ICrewEvent => {
      let index = 0;
      // check each pairing of each crew member for focusedPairing
      for (const crewMember of crew) {
        const crewId = isIPairingPosition(crewMember) ? `${crewMember.position}-${index}` : crewMember._id;
        const crewMemberPairings: ICrewPairing = crewPairings[crewId];
        if (crewMemberPairings?.pairings) {
          const foundPairing = crewMemberPairings.pairings.find((pairing) => pairing._id === pairingId);

          if (foundPairing) return { event: foundPairing, crewId, isPlanned: false };
        }
        index++;
      }

      // not found
      return null;
    },
    [crew, crewPairings]
  );

  /**
   * Changes the colour of selected events outline once
   */
  const flashSelectedEvents = useCallback(() => {
    const defaultColor = crewSchedGL?.current?.getFocusedEventsOutlineColor();
    crewSchedGL?.current?.setFocusedEventsOutlineColor(!defaultColor);
    animate();
  }, [animate]);

  // handles state change of focusCrewSchedPairing to find/select and
  // jump view to event by pairingId
  useLayoutEffect(() => {
    if (crewSchedGL && !pairingEditorView && focusCrewSchedPairing?.length > 0) {
      const pairingEvent = getPairingEventFromId(focusCrewSchedPairing);
      if (pairingEvent) {
        const { event } = pairingEvent;
        moveCameraToCrewMember(pairingEvent.crewId);
        setCrewSchedTime(event.start?.valueOf(), event.end?.valueOf());
        crewSchedGL?.current?.setFocusedEvents([pairingEvent]);

        // flash newly selected event outline
        const changeFocusedEventsOutline = setInterval(() => {
          flashSelectedEvents();
        }, 200);
        setTimeout(() => {
          clearInterval(changeFocusedEventsOutline);
          crewSchedGL?.current?.setFocusedEvents([]);
          animate();
        }, 1200);

        animate();
      } else {
        // not found
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "warning",
            message: "Unable to focus pairing in some timeline(s), please search the timeline or change crew filters.",
          })
        );
      }
      // reset redux
      dispatch(setFocusedCrewSchedPairing(null));
    }
  }, [
    animate,
    dispatch,
    focusCrewSchedPairing,
    getPairingEventFromId,
    pairingEditorView,
    moveCameraToCrewMember,
    setCrewSchedTime,
    flashSelectedEvents,
  ]);

  // #region Resize
  const canvasDim = useRef<DOMRect>(mount.current?.getBoundingClientRect());
  useLayoutEffect(() => {
    setTimeout(() => {
      const newDim = mount?.current?.getBoundingClientRect();
      if (!newDim || (newDim.width === 0 && newDim.height === 0)) {
        return;
      }
      if (newDim.width !== canvasDim.current?.height || newDim.height !== canvasDim.current?.height) {
        canvasDim.current = newDim;
        crewSchedGL.current?.setViewport(newDim.width, newDim.height);
        start();
      }
    }, 0);
  }, [crew, path, headerChanged, canvasDim, crewSchedGL, mount, start]);

  // SHIFT/CTRL Day selection handler.
  // Start of day (utc) epochs will not be passed to simplify detecting of duplicate selections.
  const updateSelectedCrewDays = useCallback(
    (event: React.MouseEvent): void => {
      const { ctrlKey, shiftKey } = event;
      event.preventDefault();
      const hasHovered = crewSchedGL.current?.getHoveredEvents()?.length > 0;
      const [mouseCrew, mouseTime] = getCrewMemberAndTimeFromMouse(event.clientX, event.clientY);
      const sameCrew = selectedCrewDays?.crewId === mouseCrew;
      const daySelected = moment.utc(mouseTime).tz(displayTimezone.timezoneId).startOf("day")?.valueOf();
      // BIDDING RELATED CREW SELECTION (on right click)
      if (selectedBidPackage && bidAssignmentIsDirty && mouseCrew !== selectedBiddingCrewMember?._id) {
        setActiveEventModal({ type: EventModalTypes.BidAssignmentDirty });
        return; // exit this function if the modal is popped up.
      } else if (!!selectedBidPackage && !bidAssignmentIsDirty && mouseCrew !== selectedBiddingCrewMember?._id) {
        dispatch(toggleSelectedCrewMemberAction(mouseCrew, true, true)); // needed to force expansion while in bidding mode.
      }

      // DESELECT SELECTED INDIVIDUAL CREW DAY
      if (
        !hasHovered &&
        !!mouseCrew &&
        mouseCrew === selectedCrewDays?.crewId &&
        daySelected === selectedCrewDays?.selectedTimes[0]
      ) {
        dispatch(setSelectedCrewDays(null));
      } else if (ctrlKey && !shiftKey && !hasHovered && sameCrew) {
        // SELECT INDIVIDUAL CREW DAY
        const match = selectedCrewDays?.selectedTimes.findIndex((time) => time === daySelected);
        const newTimes = [...selectedCrewDays.selectedTimes];
        if (match !== -1) {
          newTimes.splice(match, 1); // If found, remove it
        } else {
          newTimes.push(daySelected); // if not, add it
        }
        dispatch(
          setSelectedCrewDays({ crewId: mouseCrew, selectedTimes: newTimes, timezone: displayTimezone.timezoneId })
        );
      } else if (!ctrlKey && shiftKey && !hasHovered && sameCrew) {
        // SELECT RANGE OF CREW DAYS
        // Sort all selected times from earliest to latest
        const sortedTime = [...selectedCrewDays.selectedTimes, daySelected].sort();
        if (sortedTime.length >= 2) {
          const earliestDay = sortedTime[0];
          const latestDay = sortedTime.slice(-1)[0];
          const daysAfterFirst = moment(latestDay).diff(earliestDay, "days") + 1;
          const fillTimes = [earliestDay];
          for (let i = 1; i < daysAfterFirst; i++) {
            // Fill in the shift selection with epoch values for all days between min/max
            const newEpochValue = earliestDay + 86400000 * i;
            //fillTimes.push(earliestDay.utc(false).startOf("day").add(1, "day")?.valueOf());
            fillTimes.push(newEpochValue);
          }
          dispatch(
            setSelectedCrewDays({ crewId: mouseCrew, selectedTimes: fillTimes, timezone: displayTimezone.timezoneId })
          );
        } else {
          const newTimes = [...selectedCrewDays.selectedTimes, daySelected];
          dispatch(
            setSelectedCrewDays({ crewId: mouseCrew, selectedTimes: newTimes, timezone: displayTimezone.timezoneId })
          );
        }
      } else if (!hasHovered) {
        dispatch(
          setSelectedCrewDays({ crewId: mouseCrew, selectedTimes: [daySelected], timezone: displayTimezone.timezoneId })
        );
      }

      // OPEN CONTEXT MENU
      dispatch(openContextMenu(`${id}`));
    },
    [
      selectedCrewDays?.crewId,
      selectedCrewDays?.selectedTimes,
      selectedBidPackage,
      bidAssignmentIsDirty,
      selectedBiddingCrewMember?._id,
      dispatch,
      id,
      setActiveEventModal,
      displayTimezone,
    ]
  );

  // ensure outlined events are on the timeline
  // redraw outlines when things change
  useEffect(() => {
    if (crewSchedGL && !pairingEditorView && selectedCrewEvents.length > 0) {
      const visibleEvents = selectedCrewEvents.filter((crewEvent) => {
        let index = 0;
        return crew.find((crewMember) => {
          const crewId = isIPairingPosition(crewMember) ? `${crewMember.position}-${index++}` : crewMember._id;
          return crewId === crewEvent.crewId;
        });
      });
      crewSchedGL?.current?.setOutlinedEvents(visibleEvents);
      animate();
    }
  }, [selectedCrewEvents, crew, pairingEditorView, dispatch, animate]);

  // If displayTimezone is anything but UTC, use it's assigned timezoneId, otherwise use crewTime (selected base).
  const glTimezone: IBaseTime = useMemo(() => {
    const baseTime =
      displayTimezone.name !== "UTC"
        ? {
            id: displayTimezone.ident,
            label: displayTimezone.name,
            isUTC: false,
            timezoneId: displayTimezone.timezoneId,
          }
        : crewBase.value;
    return baseTime;
  }, [crewBase, displayTimezone]);

  useEffect(() => {
    crewSchedGL?.current?.setBaseTime(glTimezone);
    if (!isAnimating) {
      start();
    }
  }, [glTimezone, start]);

  useEffect(() => {
    const currentMode = crewSchedGL?.current?.bidAssignmentMode;
    const bidAssignModeActive = !!selectedBidPackage?.bidPackage?._id;

    // ONLY ACTION THESE WHEN SWITCHING TO/FROM BIDDING MODE
    if (bidAssignModeActive !== currentMode && !pairingEditorView) {
      crewSchedGL?.current?.setBidAssignmentMode(bidAssignModeActive);
      // If there are any expanded or selected crew, clear them after switching modes.
      if (!selectedBiddingCrewMember) {
        dispatch(deselectCollapseAllRows());
      }
      // If switching mode to bidding, set timeline scale to match bidding block times (plus minus a day).
      if (bidAssignModeActive) {
        const newStartTime = new Date(selectedBidPackage?.bidPackage?.bidBlockStart)?.valueOf();
        const newEndTime = new Date(selectedBidPackage?.bidPackage?.bidBlockEnd)?.valueOf();
        crewSchedGL?.current?.setBiddingBlockTimes([newStartTime, newEndTime]);
      } else {
        crewSchedGL?.current?.setBiddingBlockTimes(null);
      }
    }

    // ACTION THESE WHEN BIDDING MODE ACTIVE AND SELECTED CREW CHANGE
    if (currentMode && !!crewBids && !!selectedBiddingCrewMember) {
      crewSchedGL?.current?.updateCrewBidPrefRanks(selectedBiddingCrewMember?._id);
      crewSchedGL?.current?.updateCrewBidPairings(crewBids, awardedBidsVisible);
      setLastUpdatedCrew(Date.now());
    }
  }, [selectedBidPackage, selectedBiddingCrewMember, dispatch, pairingEditorView, crewBids, awardedBidsVisible]);

  useEffect(() => {
    if (selectedBidPackage && biddingSelectedNFACounts) {
      crewSchedGL?.current?.setBidPackageNFACounts(biddingSelectedNFACounts);
      // Update viewport if nfa counts are modified (so that labels are properly shifted).
      const newDim = mount?.current?.getBoundingClientRect();
      crewSchedGL.current?.setViewport(newDim.width, newDim.height);
      if (!isAnimating) {
        animate();
      }
    } else if (!selectedBidPackage && crewSchedGL?.current?.biddingNFACounts) {
      // If selected package is unloaded, cleared NFA counts from webgl component.
      crewSchedGL?.current?.setBidPackageNFACounts(null);
      if (!isAnimating) {
        animate();
      }
    }
  }, [selectedBidPackage, biddingSelectedNFACounts, animate, mount]);
  useEffect(() => {
    // Store bid rank settings if available or reset null.
    crewSchedGL?.current?.setBidRankSettings(bidRankSettings?.length ? bidRankSettings : null);
  }, [bidRankSettings]);

  useEffect(() => {
    // Pass hideNfaPriority setting to the GL renderer.
    crewSchedGL?.current?.setHideNfaPriority(hideNfaPriority);
  }, [hideNfaPriority]);

  // If any of the items included here are modified, redraw the webgl component.
  useLayoutEffect(() => {
    if (!isAnimating) {
      start();
    }
  }, [crewPairings, crew, crewNFAs, crewViolations, crewPlannedEvents, start]);
  return (
    <OCCrewScheduleCanvas ref={drop} container direction="column">
      <div
        key={`webgl_context_${1 + webGLLostContextCount}`} // remount this component if context is lost.
        className="crew_schedGL"
        ref={mount}
        tabIndex={0}
        id={props.id + "-crewSchedGL"}
        data-testid="crew-schedGL"
        onWheel={handleWheel(
          crewSchedGL,
          mount,
          liveLoad,
          isCameraLocked,
          isAnimating,
          start,
          id,
          id === timelineLink?.activeWindowId ? dispatch : null,
          dataLoadedCheck
        )}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onContextMenu={updateSelectedCrewDays}
        onKeyDown={handleKeyPress(crewSchedGL, dispatch)}
        onPointerEnter={handleMouseEnter}
        onPointerLeave={handleMouseLeave}
        onPointerUp={() => {
          if (captureMode && mount) {
            exitCaptureMode();
          }
        }}
        //onFocus={() => setCrewCanvasFocus(true)}
        //onBlur={() => setCrewCanvasFocus(false)}
      >
        {!pairingEditorView && (
          <Grid container className="linked">
            <IconButton
              className="link_button"
              onMouseDown={(event: any) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onMouseUp={(event: any) => {
                event.preventDefault();
                event.stopPropagation();
                setShowTimelineDatePickers(false);
                if (timelinesLinked) {
                  dispatch(setTimelineLink(null)); // disable link mode.
                } else {
                  const [start, end] = crewSchedGL.current.getTimeRange();
                  dispatch(setTimelineLink(start, end, id));
                }
              }}
              tooltipProps={{
                title: timelinesLinked
                  ? "Unlink crew schedule timelines"
                  : "Link all crew schedule timelines to active.",
                enterDelay: 1000,
                placement: "top",
              }}
            >
              {timelinesLinked ? <Link color="primary" /> : <LinkOpen />}
            </IconButton>
          </Grid>
        )}
      </div>
      <CrewScheduleViolations
        lastUpdatedCrew={lastUpdatedCrew}
        crewSchedGL={crewSchedGL.current}
        start={start}
        isAnimating={isAnimating}
      />
      <CrewScheduleQualifications
        lastUpdatedCrew={lastUpdatedCrew}
        crewSchedGL={crewSchedGL.current}
        start={start}
        isAnimating={isAnimating}
        hoveredViolation={hoveredViolation}
        anchorEl={mount.current}
      />
      {!pairingEditorView && (
        <>
          <SelectedCrewEvents crewSchedGL={crewSchedGL.current} start={start} isAnimating={isAnimating} />
          <SelectedCrewDays crewSchedGL={crewSchedGL.current} start={start} isAnimating={isAnimating} crew={crew} />
          {crewScheduleHideToolTips !== true && (
            <HoveredCrewSchedEvents anchorEl={mount.current} selectedCrewEvents={selectedCrewEvents} />
          )}
        </>
      )}

      <FlightTimelineDatePicker
        anchorID={`${props.id}-crewSchedGL`}
        timelineGL={crewSchedGL.current}
        display={showTimelineDatePickers}
        setDisplay={setShowTimelineDatePickers}
        dataLoadedCheck={dataLoadedCheck}
        refreshTimeline={animate}
      />

      {!hideHoveredTimeIndicator && crewSchedGL.current && (
        <HoveredTime setHoveredTime={crewSchedGL.current.setHoveredTime} isAnimating={isAnimating} start={start} />
      )}
      {crewDataLoaded && (
        <div className={"loadingData"}>
          <SpinnerProgress loading={true} message={"Loading Crew Data"} fullscreen={false} />
        </div>
      )}
      {
        // Modal that will popup if users attempt to close the mosaic window with dirty bid assignments.
        setCloseWarning && <WarningModal displayModal={bidAssignmentIsDirty} setCloseWarning={setCloseWarning} />
      }
    </OCCrewScheduleCanvas>
  );
};
